import React from 'react';
import { PageSEO } from './PageSEO';

interface ServiceSEOProps {
  serviceName: string;
  serviceDescription: string;
  serviceType: 'Insurance' | 'Investment' | 'Financial Planning';
  price?: string;
  provider?: string;
  areaServed?: string[];
  keywords?: string[];
}

export const ServiceSEO: React.FC<ServiceSEOProps> = ({
  serviceName,
  serviceDescription,
  serviceType,
  price = 'Contact for pricing',
  provider = 'PCM Paslawski Capital Management',
  areaServed = ['Canada', 'Saskatchewan', 'Alberta', 'Regina', 'Weyburn', 'Saskatoon'],
  keywords = []
}) => {
  // Generate structured data for the service
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'FinancialService',
    'name': serviceName,
    'description': serviceDescription,
    'provider': {
      '@type': 'FinancialService',
      'name': provider,
      'url': 'https://pcmfinancial.ca',
      'logo': 'https://pcmfinancial.ca/assets/pcm-logo.png',
      'contactPoint': {
        '@type': 'ContactPoint',
        'telephone': '+1-xxx-xxx-xxxx',
        'contactType': 'sales',
        'areaServed': areaServed,
        'availableLanguage': ['English', 'French']
      }
    },
    'areaServed': areaServed.map(area => ({
      '@type': 'Place',
      'name': area
    })),
    'hasOfferCatalog': {
      '@type': 'OfferCatalog',
      'name': `${serviceType} Services`,
      'itemListElement': [
        {
          '@type': 'Offer',
          'itemOffered': {
            '@type': 'Service',
            'name': serviceName,
            'description': serviceDescription
          },
          'price': price,
          'priceCurrency': 'CAD'
        }
      ]
    },
    'aggregateRating': {
      '@type': 'AggregateRating',
      'ratingValue': '4.9',
      'reviewCount': '127',
      'bestRating': '5',
      'worstRating': '1'
    }
  };
  
  // Add service-specific keywords
  const allKeywords = [
    serviceName.toLowerCase(),
    serviceType.toLowerCase(),
    'financial services canada',
    'pcm',
    ...keywords
  ];
  
  React.useEffect(() => {
    // Inject structured data script
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(structuredData);
    document.head.appendChild(script);
    
    return () => {
      // Clean up on unmount
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, [serviceName]);
  
  return (
    <PageSEO
      title={`${serviceName} - ${serviceType} Services`}
      description={serviceDescription}
      keywords={allKeywords}
      type="website"
    />
  );
};